import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { sortArrayOfObjectsByStringProperty } from '@core/util/array';
import { createId } from '@core/util/file';
import {
	Document as MatterDocument,
	GlobalDocument,
	MatterFileType,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessDocumentTemplatesService } from '@repos/document-templates/business-document-templates.service';
import { MatterDocumentsRepoService } from '@repos/matter-documents/matter-documents-repo.service';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import { DocumentsDispatch } from '@state/matter/documents';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { format } from 'date-fns';
import LogRocket from 'logrocket';
import { map, take } from 'rxjs/operators';
import { DocumentService } from 'src/app/shared/services/document/document.service';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { BusinessDocumentV2, DocumentV2Html } from '@models/documents';
import { Select } from '@ngxs/store';
import { BusinessDocumentsState } from '@state/business/documents';
import { Observable } from 'rxjs';
import {
	getDocumentHtmlId,
	transformBusinessDocumentToDocument,
} from '@core/util/documents';
import { BusinessDocumentHtmlRepoService } from '@repos/business-document-html/business-document-html.service';
export type NewDocumentFilteringFn = (document: BusinessDocumentV2) => boolean;

export interface NewDocumentDialogData {
	// target folder id
	folderId: string;

	// optional filtering function
	preFiterFn?: NewDocumentFilteringFn;

	// if true, the user will be making a new document
	createNewSelected?: boolean;
}

@UntilDestroy()
@Component({
	selector: 'app-new-document',
	styleUrls: ['./new-document-dialog.component.scss'],
	templateUrl: './new-document-dialog.component.html',
	animations: [openClose()],
	standalone: false,
})
export class NewDocumentDialogComponent implements OnInit {
	readonly BlankDocument: GlobalDocument;
	businessDocuments: BusinessDocumentV2[];
	creating: boolean;
	id = createId();
	@SelectSnapshot(AuthState.getLoggedInUser) loggedInUser: AuthUser;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@Select(BusinessDocumentsState.getAll) businessDocuments$: Observable<
		BusinessDocumentV2[]
	>;

	error = false;
	ICON = ICON;
	selectedDocuments: { [id: string]: BusinessDocumentV2 } = {};
	createNewSelected = false;
	newDocumentName: string;
	selectADocumentMessage = false;
	documentFilter: string;
	filteredBusinessDocuments: BusinessDocumentV2[] = [];
	preFiterFn!: NewDocumentFilteringFn;

	constructor(
		private businessDocumentTemplatesService: BusinessDocumentTemplatesService,
		@Inject(MAT_DIALOG_DATA) public data: NewDocumentDialogData,
		private dialogRef: MatDialogRef<NewDocumentDialogComponent>,
		private documentsDispatch: DocumentsDispatch,
		private navigationService: NavigationService,
		private documentRepo: MatterDocumentsRepoService,
		private documentService: DocumentService,
		private businessTemplateHtmlRepoService: BusinessDocumentHtmlRepoService,
	) {
		this.BlankDocument = {
			...documentService.getDefaultDocument(),
			title: 'Untitled Document',
		};
	}

	ngOnInit(): void {
		this.dialogRef.addPanelClass('no-padding');
		this.preFiterFn = this.data.preFiterFn;
		this.createNewSelected = this.data.createNewSelected || false;

		this.businessDocuments$
			.pipe(
				untilDestroyed(this),
				map((documents) => documents as BusinessDocumentV2[]),
				map((documents) => {
					const { preFiterFn } = this;
					if (preFiterFn) {
						return documents.filter((doc) => preFiterFn(doc));
					}

					return documents;
				}),
				map((documents) =>
					documents
						.filter((document) => document?.title)
						.filter(
							(document) =>
								document?.matterType?.length === 0 ||
								document?.matterType?.includes(this.fileType),
						)
						.filter((document) => !document.groupType)
						.sort(sortArrayOfObjectsByStringProperty('title')),
				),
			)
			.subscribe((documents) => {
				this.businessDocuments = documents;
				this.filterDocuments();
			});
		this.creating = false;
	}

	cancelModal(): void {
		this.dialogRef.close();
	}

	async createDocument(): Promise<void> {
		this.error = false;
		this.selectADocumentMessage = false;

		if (
			!this.createNewSelected &&
			this.objectKeys(this.selectedDocuments).length === 0
		) {
			this.selectADocumentMessage = true;
		}

		const _documents: { [id: string]: MatterDocument } = {};
		this.creating = true;

		if (this.createNewSelected) {
			const title =
				this.newDocumentName?.length > 0
					? this.newDocumentName
					: 'New Document';
			const id = createId();

			const doc = {
				...this.documentService.getDefaultDocument(),
				title,
				author: this.loggedInUser.uid,
				date: format(Date.now(), 'yyyy-MM-dd'),
				createdAt: new Date().getTime(),
				folderId: this.data.folderId,
				wasVirtualized: false,
				virtual: false,
				id,
			};
			_documents[id] = doc;
		} else {
			const docs = Object.values(this.selectedDocuments).filter(
				(doc) => doc !== undefined,
			);

			for await (const x of docs) {
				const id = createId();
				const htmlId = getDocumentHtmlId(x);
				const htmlData = (
					await this.businessTemplateHtmlRepoService
						.Get(htmlId)
						.pipe(take(1))
						.toPromise()
				).data() as DocumentV2Html;
				_documents[id] = {
					...this.documentService.getDefaultDocument(),
					...transformBusinessDocumentToDocument(x),
					author: this.loggedInUser.uid,
					date: format(Date.now(), 'yyyy-MM-dd'),
					folderId: this.data.folderId,
					source: x.id,
					htmlId: htmlId,
					createdAt: new Date().getTime(),
					id,
					updatedAt: undefined,
					wasVirtualized: false,
					virtual: false,
					html: htmlData.html,
				};
			}
		}
		try {
			await this.documentRepo.createDocuments(_documents, this.matterId);
			const keys = Object.keys(_documents);
			if (keys?.length === 1) {
				this.navigationService.goToMatter_Documents_Doc(this.matterId, keys[0]);
			}

			this.dialogRef.close();
		} catch (err) {
			this.error = true;
			LogRocket.error('Unable to create documents', err);
		} finally {
			this.creating = false;
		}
	}

	toggleSelectedDocument(document) {
		this.createNewSelected = false;

		if (this.selectedDocuments[document.id]) {
			this.selectedDocuments[document.id] = undefined;
		} else {
			this.selectedDocuments[document.id] = document;
		}
	}

	selectAddNew() {
		this.createNewSelected = true;
		this.selectedDocuments = {};
	}

	filterDocuments() {
		if (this.documentFilter?.length > 0) {
			this.filteredBusinessDocuments = this.businessDocuments.filter((x) => {
				return x.title
					?.toLowerCase()
					.includes(this.documentFilter?.toLowerCase());
			});
		} else {
			this.filteredBusinessDocuments = this.businessDocuments;
		}
	}

	getSelected(id: string): boolean {
		return this.selectedDocuments[id] !== undefined;
	}

	get documentTotals() {
		return Object.keys(this.selectedDocuments).length;
	}

	objectKeys(obj: { [id: string]: BusinessDocumentV2 }): string[] {
		return Object.keys(obj).filter((x) => obj[x] !== undefined);
	}
}
